// useNavigationBlocker.js
import { useEffect } from "react";
import { useLocation, matchPath } from "react-router-dom";

/**
 * Blocks browser unload events (refresh, tab close) when needed.
 * Handles Safari quirks with a 1-second delay before enabling.
 * Does NOT interfere with React Router navigation.
 *
 * @param {Array} ignoredRoutes - Static paths to ignore (no blocking)
 * @param {Array} dynamicIgnoredRoutes - Dynamic path patterns to ignore
 */
export default function useNavigationBlocker(
  ignoredRoutes = [],
  dynamicIgnoredRoutes = []
) {
  const location = useLocation();
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Determine if the current page should trigger unload blocking
  const shouldBlock = () => {
    if (ignoredRoutes.includes(location.pathname)) return false;
    return !dynamicIgnoredRoutes.some((pattern) =>
      matchPath({ path: pattern, end: false }, location.pathname)
    );
  };

  // Handle beforeunload blocking with Safari 1-second delay
  useEffect(() => {
    if (!shouldBlock()) return; // skip if route is ignored

    const enableBlocking = () => {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = ""; // required for browser dialog
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    };

    let cleanup;
    if (isSafari) {
      const timer = setTimeout(() => {
        cleanup = enableBlocking();
      }, 1000);
      return () => {
        clearTimeout(timer);
        if (cleanup) cleanup();
      };
    } else {
      cleanup = enableBlocking();
      return cleanup;
    }
  }, [location.pathname, isSafari]);
}
